import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RGBColor } from 'react-color';

import { BACKGROUND_TYPE_TABS } from '../../../../constants/templates';
import { useTemplateOverrideMode } from '../../../../hooks/useTemplateOverrideMode';
import { ImageFitEnum, ISegmentedControlOption } from '../../../../types/shell';
import { IBaseUploadControlProps } from '../../../../types/templates';
import { BackgroundTypesEnum } from '../../../../types/upload';
import { findScrollableParent, scrollElementIntoView } from '../../../../utils/helpers';
import { SegmentedControl } from '../../_controls_';
import { FormLabel } from '../FormLabel';
import { BackgroundImageUploader } from './BackgroundImageUploader';
import { ColorPickerWithReset } from './ColorPickerWithReset';

interface IProps extends IBaseUploadControlProps {
  globalColor?: RGBColor;
  overrideColor?: RGBColor;
  defaultColor: RGBColor;
  globalImageUrl?: string;
  overrideImageUrl?: string;
  defaultImageUrl?: string;
  defaultImageFit?: ImageFitEnum;
  globalImageFit?: ImageFitEnum;
  overrideImageFit?: ImageFitEnum;
  backgroundType?: BackgroundTypesEnum;
  onBackgroundTypeChange?: (type: BackgroundTypesEnum) => void;
  onColorChange: (color: RGBColor) => void;
  onColorReset: () => void;
  onImageDrop?: (files: File[]) => void;
  onImageUpload?: (url: string) => void;
  onImageRemove: () => void;
  onImageFitChange: (fit: ImageFitEnum) => void;
  onClearColorOverride?: () => void;
  onClearImageOverride?: () => void;
  onClearFitOverride?: () => void;
  onResetImageToGlobal?: () => void;
}

export const HeroBackgroundControl: React.FC<IProps> = ({
  mode,
  backgroundType,
  onBackgroundTypeChange,
  globalColor,
  overrideColor,
  defaultColor,
  globalImageUrl,
  overrideImageUrl,
  defaultImageUrl,
  defaultImageFit,
  globalImageFit,
  overrideImageFit,
  onColorChange,
  onColorReset,
  onImageDrop,
  onImageUpload,
  onImageRemove,
  onImageFitChange,
  onClearColorOverride,
  onClearImageOverride,
  onResetImageToGlobal,
  dataAction,
}) => {
  const { isOverridden: isImageOverridden } = useTemplateOverrideMode({
    mode,
    overrideValue: overrideImageUrl,
    onClearOverride: onClearImageOverride,
  });

  const displayImageUrl = useMemo(() => {
    if (isImageOverridden) {
      return overrideImageUrl;
    }
    return globalImageUrl || '';
  }, [isImageOverridden, overrideImageUrl, globalImageUrl]);

  const isImageRemovedFromGlobal = !displayImageUrl && !!globalImageUrl && isImageOverridden;

  const [localBackgroundType, setLocalBackgroundType] = useState<BackgroundTypesEnum>(() =>
    displayImageUrl || isImageRemovedFromGlobal ? BackgroundTypesEnum.Image : BackgroundTypesEnum.Color,
  );
  const backgroundSectionRef = useRef<HTMLDivElement>(null);
  const prevDisplayImageUrlRef = useRef(displayImageUrl);

  useEffect(() => {
    const prev = prevDisplayImageUrlRef.current;
    prevDisplayImageUrlRef.current = displayImageUrl;

    // Sync tab when image URL is loaded externally (e.g. from backend)
    // Do NOT switch to Color when the user removes the image — keep the Image
    // tab visible so they see the placeholder and can reset to default.
    if (!prev && displayImageUrl) {
      setLocalBackgroundType(BackgroundTypesEnum.Image);
      onBackgroundTypeChange?.(BackgroundTypesEnum.Image);
    }
  }, [displayImageUrl, onBackgroundTypeChange]);

  // Controlled when the parent tracks the active selection; local state is the fallback.
  const resolvedBackgroundType = backgroundType ?? localBackgroundType;

  const backgroundTypeOptions: ISegmentedControlOption<BackgroundTypesEnum>[] = useMemo(() => {
    return BACKGROUND_TYPE_TABS.map((tab) => ({
      label: tab.label,
      value: tab.value,
      icon: <tab.icon fontSize="small" />,
      dataAction: `${dataAction}_type_${tab.value.toLowerCase()}`,
    }));
  }, [dataAction]);

  const scrollToBackgroundSection = useCallback(() => {
    if (!backgroundSectionRef.current) {
      return;
    }

    // Delay to ensure Image component is rendered
    setTimeout(() => {
      const element = backgroundSectionRef.current;
      if (!element) {
        return;
      }

      const scrollableParent = findScrollableParent(element);

      if (scrollableParent) {
        scrollElementIntoView(element, scrollableParent);
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50);
  }, []);

  const handleBackgroundTypeChange = useCallback(
    (value: BackgroundTypesEnum) => {
      setLocalBackgroundType(value);
      onBackgroundTypeChange?.(value);

      if (value === BackgroundTypesEnum.Image) {
        scrollToBackgroundSection();
      }
    },
    [scrollToBackgroundSection, onBackgroundTypeChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <FormLabel label="Hero background">
          <SegmentedControl
            options={backgroundTypeOptions}
            value={resolvedBackgroundType}
            onChange={handleBackgroundTypeChange}
            dataAction={`${dataAction}_type`}
          />
        </FormLabel>
      </Box>
      <Box ref={backgroundSectionRef}>
        {resolvedBackgroundType === BackgroundTypesEnum.Color ? (
          <ColorPickerWithReset
            mode={mode}
            globalValue={globalColor}
            overrideValue={overrideColor}
            defaultValue={defaultColor}
            onChange={onColorChange}
            onReset={onColorReset}
            onClearOverride={onClearColorOverride}
            dataAction={`${dataAction}_color`}
          />
        ) : (
          <BackgroundImageUploader
            mode={mode}
            globalImageUrl={globalImageUrl}
            overrideImageUrl={overrideImageUrl}
            defaultImageUrl={defaultImageUrl}
            defaultImageFit={defaultImageFit}
            globalImageFit={globalImageFit}
            overrideImageFit={overrideImageFit}
            onImageDrop={onImageDrop}
            onImageUpload={onImageUpload}
            onImageRemove={onImageRemove}
            onImageFitChange={onImageFitChange}
            onClearImageOverride={onClearImageOverride}
            onResetImageToGlobal={onResetImageToGlobal}
            dataAction={dataAction}
          />
        )}
      </Box>
    </Box>
  );
};
